/**
 * The plus in the send bar. Desktop and tablet open the picker; the phone
 * opens a two-item row (Take photo / Choose a file) inside the bar, because
 * `capture` is the only way to reach the rear camera directly.
 *
 * Drop and paste are wired on the channel view (Task 10); every route runs
 * `acceptFiles`, so one notice text covers all four.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@roger/ui-components';
import { Camera, Paperclip, Plus } from 'lucide-react';

import { useIsMobile } from '../../hooks/useIsMobile';
import { acceptFiles } from '../../utils/attachments';

interface AttachButtonProps {
  existingCount: number;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  onNotice: (message: string) => void;
}

export function AttachButton({
  existingCount,
  disabled = false,
  onFiles,
  onNotice,
}: AttachButtonProps) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const pickerRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  // Esc closes the row wherever focus is. Capture phase plus preventDefault:
  // ChatPanel's document listener skips a prevented Escape, so the panel
  // stays open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [menuOpen]);

  // Going offline with the phone row open closes it, so no stale action stays.
  useEffect(() => {
    if (disabled) setMenuOpen(false);
  }, [disabled]);

  const accept = useCallback(
    (incoming: File[]) => {
      setMenuOpen(false);
      // A picker opened before the stream stopped still reports its files.
      if (disabled) return;
      const accepted = acceptFiles(incoming, existingCount, onNotice);
      if (accepted.length > 0) onFiles(accepted);
    },
    [disabled, existingCount, onFiles, onNotice]
  );

  const onPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    accept(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  return (
    // flex-col-reverse: the phone row follows the plus in the DOM (tab order
    // plus, Take photo, Choose a file) and shows above it, so the send bar's
    // toolbar row keeps send on the baseline instead of wrapping.
    <div className='flex flex-col-reverse items-start gap-2'>
      <Button
        size='icon'
        variant='ghost'
        aria-label='Attach a photo or file'
        disabled={disabled}
        onClick={() =>
          isMobile ? setMenuOpen(open => !open) : pickerRef.current?.click()
        }
      >
        <Plus className='h-4 w-4' />
      </Button>

      {isMobile && menuOpen && !disabled && (
        <div className='flex flex-wrap gap-2'>
          <Button
            size='sm'
            variant='outline'
            onClick={() => cameraRef.current?.click()}
          >
            <Camera className='mr-2 h-4 w-4' /> Take photo
          </Button>
          <Button
            size='sm'
            variant='outline'
            onClick={() => pickerRef.current?.click()}
          >
            <Paperclip className='mr-2 h-4 w-4' /> Choose a file
          </Button>
        </div>
      )}

      <input
        ref={pickerRef}
        data-testid='attach-input'
        type='file'
        multiple
        className='hidden'
        onChange={onPicked}
      />
      <input
        ref={cameraRef}
        data-testid='attach-camera-input'
        type='file'
        accept='image/*'
        capture='environment'
        className='hidden'
        onChange={onPicked}
      />
    </div>
  );
}
